import React, { useState } from "react";
import { X, Plus, Send } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

const DISCIPLINES = [
  "Powerlifting",
  "CrossFit",
  "Yoga",
  "Running",
  "HIIT",
  "Bodybuilding",
  "Calisthenics",
  "Olympic Lifting",
  "Boxing",
  "Pilates",
  "Spin",
  "Hiking",
];

const ICONS = ["Flame", "Dumbbell", "Activity", "Sparkles"];

const fields = {
  groups: [
    { key: "name", label: "Crew name", placeholder: "ATL Strength Crew", required: true },
    {
      key: "description",
      label: "What's this crew about?",
      placeholder: "Saturday meets, weekday programming…",
      type: "textarea",
      required: true,
    },
    {
      key: "discipline",
      label: "Primary discipline",
      placeholder: "Powerlifting",
      type: "select",
      options: DISCIPLINES,
      required: true,
    },
    { key: "location_city", label: "City", placeholder: "Atlanta, GA" },
    { key: "cover", label: "Cover image URL (optional)", placeholder: "https://…" },
  ],
  events: [
    { key: "title", label: "Event title", placeholder: "Open Lift Night", required: true },
    {
      key: "description",
      label: "Details",
      placeholder: "What to expect, what to bring…",
      type: "textarea",
      required: true,
    },
    { key: "starts_at_date", label: "Date", type: "date", required: true },
    { key: "starts_at_time", label: "Time", type: "time", required: true },
    { key: "location", label: "Location", placeholder: "Iron Republic, Atlanta", required: true },
    { key: "host_name", label: "Host name", placeholder: "Auto: your name" },
    { key: "cover", label: "Cover image URL (optional)", placeholder: "https://…" },
  ],
  challenges: [
    { key: "title", label: "Challenge title", placeholder: "30-Day Show Up Streak", required: true },
    {
      key: "description",
      label: "What's the rule?",
      placeholder: "Train at least 5 days a week…",
      type: "textarea",
      required: true,
    },
    {
      key: "duration_days",
      label: "Duration (days)",
      type: "number",
      placeholder: "30",
      required: true,
    },
    { key: "reward", label: "Reward / badge", placeholder: "Founder badge" },
    { key: "icon", label: "Icon", type: "select", options: ICONS },
  ],
};

const labels = {
  groups: { title: "New Crew", cta: "Create Crew" },
  events: { title: "New Event", cta: "Host Event" },
  challenges: { title: "New Challenge", cta: "Launch Challenge" },
};

const PATHS = {
  groups: "/community/groups",
  events: "/community/events",
  challenges: "/community/challenges",
};

const buildPayload = (kind, form) => {
  if (kind === "events") {
    const { starts_at_date, starts_at_time, ...rest } = form;
    return {
      ...rest,
      starts_at: new Date(`${starts_at_date}T${starts_at_time}:00`).toISOString(),
    };
  }
  if (kind === "challenges") {
    return { ...form, duration_days: Number(form.duration_days) };
  }
  return { ...form };
};

const FormField = ({ field, value, onChange, kind }) => {
  const testid = `create-${kind}-${field.key}`;
  const handleChange = (e) => onChange(field.key, e.target.value);
  const safeValue = value || "";

  if (field.type === "textarea") {
    return (
      <textarea
        rows={3}
        value={safeValue}
        onChange={handleChange}
        placeholder={field.placeholder}
        data-testid={testid}
        className="cm-input resize-none"
      />
    );
  }
  if (field.type === "select") {
    return (
      <select
        value={safeValue}
        onChange={handleChange}
        data-testid={testid}
        className="cm-input"
      >
        <option value="">Choose…</option>
        {field.options.map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </select>
    );
  }
  return (
    <input
      type={field.type || "text"}
      value={safeValue}
      onChange={handleChange}
      placeholder={field.placeholder}
      data-testid={testid}
      className="cm-input"
    />
  );
};

const CommunityCreateModal = ({ kind, onClose, onCreated }) => {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const conf = fields[kind];
  const meta = labels[kind];

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    // Validate required
    for (const f of conf) {
      if (f.required && !String(form[f.key] || "").trim()) {
        toast.error(`${f.label} is required`);
        return;
      }
    }

    setSaving(true);
    try {
      await api.post(PATHS[kind], buildPayload(kind, form));
      onCreated?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Could not create.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-[#0A0A0A]/90 backdrop-blur-xl flex items-end sm:items-center justify-center p-3 sm:p-4 overflow-y-auto"
      data-testid={`create-${kind}-modal`}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="w-full max-w-md bg-[#111] border border-white/10 rounded-3xl p-6 sm:p-7 relative my-4">
        <button
          onClick={onClose}
          data-testid={`create-${kind}-close`}
          className="absolute top-4 right-4 text-neutral-400 hover:text-white"
          aria-label="Close"
        >
          <X size={20} />
        </button>

        <div className="flex items-center gap-2 mb-2">
          <Plus className="text-[#C6FF00]" size={18} />
          <p className="text-xs uppercase tracking-[0.3em] text-[#C6FF00] font-semibold">
            Create
          </p>
        </div>
        <h2 className="font-heading text-3xl mb-5">{meta.title}</h2>

        <form onSubmit={submit} className="flex flex-col gap-3">
          {conf.map((f) => (
            <div key={f.key}>
              <label className="text-[10px] uppercase tracking-[0.2em] text-neutral-400 font-semibold block mb-1.5">
                {f.label}
                {f.required && <span className="text-[#C6FF00]"> *</span>}
              </label>
              <FormField field={f} value={form[f.key]} onChange={update} kind={kind} />
            </div>
          ))}

          <button
            type="submit"
            data-testid={`create-${kind}-submit`}
            disabled={saving}
            className="mt-2 inline-flex items-center justify-center gap-2 bg-[#C6FF00] hover:bg-[#AEE600] text-[#0A0A0A] font-bold text-xs uppercase tracking-[0.25em] px-6 py-4 rounded-md transition-all disabled:opacity-60 hover:scale-[1.02]"
          >
            {saving ? "Creating…" : meta.cta} <Send size={14} />
          </button>
        </form>

        <style>{`
          .cm-input {
            width: 100%;
            background: #0A0A0A;
            border: 1px solid rgba(255,255,255,0.1);
            border-radius: 0.5rem;
            padding: 0.7rem 0.9rem;
            color: #fff;
            font-size: 0.875rem;
            outline: none;
          }
          .cm-input:focus { border-color: #C6FF00; }
          .cm-input::placeholder { color: #525252; }
        `}</style>
      </div>
    </div>
  );
};

export default CommunityCreateModal;
